/**
 * Comprehensive Dashboard Widget Registry
 * Maps all widgets to their phases, roles, categories, and metadata
 */

import { WIDGET_CATEGORIES, ROLE_TYPES, PHASE_ROLES } from './roleConstants';

/**
 * PHASE 1 WIDGETS - Seller & Company Admin (30 widgets)
 */
const PHASE_1_WIDGETS = [
    // Shipment & Label Widgets (5)
    {
        key: 'Total Labels Created',
        phase: 1,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F3F7}',
        color: '#2196F3',
        valueKey: 'totalLabels',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/labelhistory'
    },
    {
        key: 'Labels by Status',
        phase: 1,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'Shipped / Booked / Pending',
        dataKey: 'dailyShipments',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/labelhistory'
    },
    {
        key: 'Labels by Carrier',
        phase: 1,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'bar',
        subtitle: 'Shipments by carrier',
        dataKey: 'carrierDistribution',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/labelhistory'
    },
    {
        key: 'Shipping Cost Trend',
        phase: 1,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'line',
        subtitle: 'Cost trend over time',
        dataKey: 'monthlyRevenueTrend',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/labelhistory'
    },
    {
        key: 'Labels by Service Type',
        phase: 1,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'pie',
        subtitle: 'Domestic / International / Service Class',
        dataKey: 'shippingServiceBreakdown',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/labelhistory'
    },
    // Order & Outbound Widgets (4)
    {
        key: 'Total Orders',
        phase: 1,
        category: WIDGET_CATEGORIES.ORDERS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4DD}',
        color: '#4CAF50',
        valueKey: 'outboundOrders',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/outbound'
    },
    {
        key: 'Orders by Status',
        phase: 1,
        category: WIDGET_CATEGORIES.ORDERS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'New / On Hold / Shipped',
        dataKey: 'orderStatusDistribution',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/outbound'
    },
    {
        key: 'Orders by Marketplace',
        phase: 1,
        category: WIDGET_CATEGORIES.ORDERS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'bar',
        subtitle: 'Shopify / eBay / Amazon',
        dataKey: 'revenueByMarketplace',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/outbound'
    },
    {
        key: 'Order Volume Trend',
        phase: 1,
        category: WIDGET_CATEGORIES.ORDERS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'line',
        subtitle: 'Daily order count',
        dataKey: 'dailyOrders',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/outbound'
    },
    // Inventory Widgets (4)
    {
        key: 'Total SKUs',
        phase: 1,
        category: WIDGET_CATEGORIES.INVENTORY,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4E6}',
        color: '#9C27B0',
        valueKey: 'totalSKUs',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inventory'
    },
    {
        key: 'Inventory by Type',
        phase: 1,
        category: WIDGET_CATEGORIES.INVENTORY,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'pie',
        subtitle: 'Item / Bundle / Kit',
        dataKey: 'inventoryValueByCategory',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inventory',
        hidden: true
    },
    {
        key: 'Low Stock SKUs',
        phase: 1,
        category: WIDGET_CATEGORIES.INVENTORY,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{26A0}',
        color: '#FF9800',
        valueKey: 'lowStockCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inventory'
    },
    {
        key: 'Top SKUs by Quantity',
        phase: 1,
        category: WIDGET_CATEGORIES.INVENTORY,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'Top 10 by stock on hand',
        dataKey: 'topProducts',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inventory'
    },
    // Inbound Widgets (4)
    {
        key: 'Total Inbound Consignments',
        phase: 1,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F5C4}',
        color: '#3F51B5',
        valueKey: 'inboundConsignments',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inbound'
    },
    {
        key: 'Inbound by Status',
        phase: 1,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'New / Received at Warehouse',
        dataKey: 'activeShipmentsStatus',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inbound'
    },
    {
        key: 'Inbound by Carrier',
        phase: 1,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'bar',
        subtitle: 'Carrier distribution',
        dataKey: 'dailyConsignments',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inbound'
    },
    {
        key: 'Inbound by Type',
        phase: 1,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'column',
        subtitle: 'Item / Box',
        dataKey: 'inboundByConsignmentType',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/inbound'
    },
    // Returns Widgets (5)
    {
        key: 'Return Packages Received',
        phase: 1,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4A1}',
        color: '#E91E63',
        valueKey: 'returnPackagesCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/returns'
    },
    {
        key: 'Returns by Package Type',
        phase: 1,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'pie',
        subtitle: 'Package type breakdown',
        dataKey: 'returnPackagesByType',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/returns'
    },
    {
        key: 'Return Products by Status',
        phase: 1,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'column',
        subtitle: 'Processed / In Process / Shipped',
        dataKey: 'returnProductsByStatus',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/returns'
    },
    {
        key: 'Returns Billing Status',
        phase: 1,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'Billed / Unbilled / Queued',
        dataKey: 'returnBillingStatus',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/returns'
    },
    {
        key: 'Refund Request Status',
        phase: 1,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'Refund requests overview',
        dataKey: 'refundRequestStatus',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/refunds'
    },
    // Carrier Connect Widgets (3)
    {
        key: 'Total Carrier Connects',
        phase: 1,
        category: WIDGET_CATEGORIES.CARRIER,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F30A}',
        color: '#00BCD4',
        valueKey: 'totalCarrierConnects',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/carrierconnect'
    },
    {
        key: 'Connections by Carrier',
        phase: 1,
        category: WIDGET_CATEGORIES.CARRIER,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'bar',
        subtitle: 'Carrier breakdown',
        dataKey: 'carrierConnectsByCarrier',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/carrierconnect'
    },
    {
        key: 'Carrier Billing Status',
        phase: 1,
        category: WIDGET_CATEGORIES.CARRIER,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'Billed / Unbilled',
        dataKey: 'carrierConnectsByBillingStatus',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/carrierconnect'
    },
    // Tasks & Billing Widgets (5)
    {
        key: 'Open Tasks',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u2705',
        color: '#4CAF50',
        valueKey: 'openTasks',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/tasks'
    },
    {
        key: 'Tasks by Type',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'bar',
        subtitle: 'Labeling / Disposal / Repackaging',
        dataKey: 'tasksByType',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/tasks'
    },
    {
        key: 'Tasks by Priority',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'chart',
        defaultLg: 4,
        defaultMd: 6,
        defaultChartType: 'donut',
        subtitle: 'Urgent / Normal',
        dataKey: 'tasksByPriority',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/tasks'
    },
    {
        key: 'Wallet Balance',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4B5}',
        color: '#2196F3',
        valueKey: 'walletBalance',
        valueFormat: 'currency',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/fundmanagement'
    },
    {
        key: 'Spend Trend',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'line',
        subtitle: 'Debit amount over time',
        dataKey: 'spendTrend',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/fundmanagement'
    },
    {
        key: 'Recent Tasks',
        phase: 1,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'table',
        defaultLg: 12,
        defaultMd: 12,
        defaultChartType: 'table',
        subtitle: 'Task activity by type',
        dataKey: 'recentTasks',
        allowedRoles: PHASE_ROLES.PHASE_1,
        drilldownPage: '/tasks'
    }
];

/**
 * PHASE 2 WIDGETS - Warehouse Worker (6 widgets)
 */
const PHASE_2_WIDGETS = [
    {
        key: 'Picking Plan - Today',
        phase: 2,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4CB}',
        color: '#4CAF50',
        valueKey: 'pendingPickCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/orderpickingplan'
    },
    {
        key: 'Packing Plan - Today',
        phase: 2,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4E6}',
        color: '#FF9800',
        valueKey: 'pendingPackCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/orderpackingplan'
    },
    {
        key: 'Inbounds Awaiting Processing',
        phase: 2,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F5C4}',
        color: '#3F51B5',
        valueKey: 'inboundsPendingCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/inbound'
    },
    {
        key: 'Returns Pending Processing',
        phase: 2,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F4A1}',
        color: '#E91E63',
        valueKey: 'returnsPendingCount',
        valueFormat: 'number',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/returns'
    },
    {
        key: 'Open Tasks by Type',
        phase: 2,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'Task distribution for warehouse',
        dataKey: 'warehouseTasksByType',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/tasks'
    },
    {
        key: 'Company-wise Outbound Queue',
        phase: 2,
        category: WIDGET_CATEGORIES.OPERATIONS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'column',
        subtitle: "Today's pending orders by company/seller",
        dataKey: 'outboundQueueByCompany',
        allowedRoles: PHASE_ROLES.PHASE_2,
        drilldownPage: '/outbound'
    }
];

/**
 * PHASE 3 WIDGETS - Super Admin (8 widgets)
 */
const PHASE_3_WIDGETS = [
    {
        key: 'Total Active Sellers',
        phase: 3,
        category: WIDGET_CATEGORIES.ADVANCED,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F465}',
        color: '#2196F3',
        valueKey: 'activeSellersCount',
        valueFormat: 'number',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/sellers'
    },
    {
        key: 'Revenue by Company',
        phase: 3,
        category: WIDGET_CATEGORIES.ADVANCED,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'Cross-company comparison',
        dataKey: 'revenueByCompany',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/companies'
    },
    {
        key: 'Total Labels Platform-wide',
        phase: 3,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{1F3F7}',
        color: '#4CAF50',
        valueKey: 'platformTotalLabels',
        valueFormat: 'number',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/labelhistory'
    },
    {
        key: 'Negative Balance Sellers',
        phase: 3,
        category: WIDGET_CATEGORIES.ADVANCED,
        type: 'kpi',
        defaultLg: 3,
        defaultMd: 6,
        icon: '\u{26A0}',
        color: '#F44336',
        valueKey: 'negativeBalanceCount',
        valueFormat: 'number',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/fundmanagement'
    },
    {
        key: 'Refund Request Volume',
        phase: 3,
        category: WIDGET_CATEGORIES.RETURNS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'By company',
        dataKey: 'refundVolumeByCompany',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/refunds'
    },
    {
        key: 'Carrier Connect by Status',
        phase: 3,
        category: WIDGET_CATEGORIES.CARRIER,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'column',
        subtitle: 'Billing status breakdown',
        dataKey: 'carrierStatusByCompany',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/carrierconnect'
    },
    {
        key: 'Scan Forms Generated',
        phase: 3,
        category: WIDGET_CATEGORIES.SHIPMENTS,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'line',
        subtitle: 'Count over time',
        dataKey: 'scanFormsOverTime',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/scanforms'
    },
    {
        key: 'Inbound Volume by Company',
        phase: 3,
        category: WIDGET_CATEGORIES.INBOUND,
        type: 'chart',
        defaultLg: 6,
        defaultMd: 12,
        defaultChartType: 'bar',
        subtitle: 'Stacked by company',
        dataKey: 'inboundByCompany',
        allowedRoles: [ROLE_TYPES.SUPER_ADMIN],
        drilldownPage: '/inbound'
    }
];

/**
 * All widgets combined (44 total)
 */
export const ALL_DASHBOARD_WIDGETS = [
    ...PHASE_1_WIDGETS,
    ...PHASE_2_WIDGETS,
    ...PHASE_3_WIDGETS
];

/**
 * Get widgets for a specific user role
 * @param {number} roleId - The user's role ID
 * @returns {Array} Filtered list of widgets
 */
export const getWidgetsForRole = (roleId) => {
    return ALL_DASHBOARD_WIDGETS.filter(widget =>
        !widget.hidden && widget.allowedRoles?.includes(roleId)
    );
};

/**
 * Get widgets by phase
 * @param {number} phase - Phase number (1, 2, or 3)
 * @returns {Array} Widgets for that phase
 */
export const getWidgetsByPhase = (phase) => {
    if (phase === 1) return PHASE_1_WIDGETS;
    if (phase === 2) return PHASE_2_WIDGETS;
    if (phase === 3) return PHASE_3_WIDGETS;
    return [];
};

export default {
    ALL_DASHBOARD_WIDGETS,
    PHASE_1_WIDGETS,
    PHASE_2_WIDGETS,
    PHASE_3_WIDGETS,
    getWidgetsForRole,
    getWidgetsByPhase
};
